/**
 * FinancialStatementsPage — P&L · Balance Sheet · GSTR-3B
 *
 * One screen for the period-end / monthly close documents the CFO + auditor
 * actually look at.
 */
import { useState } from 'react'
import {
  Card, Col, DatePicker, Empty, InputNumber, Row, Select, Space, Spin,
  Table, Tabs, Tag, Typography,
} from 'antd'
import {
  BankOutlined, FileDoneOutlined, FundOutlined, PercentageOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const NUM = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' as const }

const fmt = (v: string | number | null | undefined, dp = 2) =>
  v == null ? '—' : Number(v).toLocaleString('en-IN',
    { minimumFractionDigits: dp, maximumFractionDigits: dp })


export default function FinancialStatementsPage() {
  const { plantId, plants, setPlantId } = usePlantId()

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <FundOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Financial Statements
      </Title>
      <Text type="secondary">
        Profit &amp; Loss · Balance Sheet · GSTR-3B — period-end documents
        for monthly close + filing.
      </Text>

      <Card style={{ marginTop: 16 }} styles={{ body: { padding: 16 } }}>
        <Row style={{ marginBottom: 16 }}>
          <Col xs={24} md="auto">
            <Select value={plantId} onChange={setPlantId}
              style={{ width: 280 }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))} />
          </Col>
        </Row>

        <Tabs
          defaultActiveKey="pl"
          items={[
            { key: 'pl', label: <span><FileDoneOutlined /> Profit &amp; Loss</span>,
              children: <PLTab plantId={plantId} /> },
            { key: 'bs', label: <span><BankOutlined /> Balance Sheet</span>,
              children: <BSTab plantId={plantId} /> },
            { key: 'gstr3b', label: <span><PercentageOutlined /> GSTR-3B</span>,
              children: <GSTR3BTab plantId={plantId} /> },
          ]}
        />
      </Card>
    </div>
  )
}


// ─── P&L ─────────────────────────────────────────────────────────────────
interface PLLine { group_code: string; group_name: string; nature: string; amount: string }
interface PLResponse {
  income_lines: PLLine[]; income_total: string
  direct_cost_lines: PLLine[]; direct_cost_total: string; gross_profit: string
  indirect_expense_lines: PLLine[]; indirect_expense_total: string
  net_profit: string
}

function PLTab({ plantId }: { plantId: string }) {
  const [from, setFrom] = useState<Dayjs>(dayjs().startOf('month'))
  const [to, setTo] = useState<Dayjs>(dayjs().endOf('month'))

  const { data, isLoading } = useQuery<PLResponse>({
    queryKey: ['pl', plantId, from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD')],
    queryFn: () => apiClient.get('/accounting/pl-statement', {
      params: {
        plant_id: plantId,
        period_from: from.format('YYYY-MM-DD'),
        period_to:   to.format('YYYY-MM-DD'),
      },
    }).then(r => r.data),
    enabled: !!plantId,
  })

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <DatePicker.RangePicker value={[from, to]}
            onChange={(v) => {
              if (v?.[0]) setFrom(v[0])
              if (v?.[1]) setTo(v[1])
            }}
            style={{ width: '100%' }} />
        </Col>
      </Row>

      {isLoading && <Spin />}
      {data && (
        <Card size="small" style={{ background: '#fafafa' }}>
          <Title level={5}>Income</Title>
          <Table size="small" pagination={false} dataSource={data.income_lines}
            rowKey="group_code" showHeader={false}
            columns={[
              { dataIndex: 'group_name' },
              { dataIndex: 'amount', align: 'right' as const,
                render: (v: string) => <span style={NUM}>₹{fmt(v, 0)}</span> },
            ]}
            summary={() => (
              <Table.Summary.Row style={{ background: '#e6fffb', fontWeight: 700 }}>
                <Table.Summary.Cell index={0}>Total Income</Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong style={{ ...NUM, color: '#52c41a' }}>₹{fmt(data.income_total, 0)}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />

          <Title level={5} style={{ marginTop: 16 }}>Direct Costs</Title>
          <Table size="small" pagination={false} dataSource={data.direct_cost_lines}
            rowKey="group_code" showHeader={false}
            columns={[
              { dataIndex: 'group_name' },
              { dataIndex: 'amount', align: 'right' as const,
                render: (v: string) => <span style={NUM}>₹{fmt(v, 0)}</span> },
            ]}
            summary={() => (
              <Table.Summary.Row style={{ background: '#fff7e6', fontWeight: 700 }}>
                <Table.Summary.Cell index={0}>Total Direct Costs</Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong style={{ ...NUM, color: '#cf1322' }}>₹{fmt(data.direct_cost_total, 0)}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />

          <Card size="small" style={{ marginTop: 12, background: '#f0f5ff' }}>
            <Row justify="space-between">
              <Col><Text strong>Gross Profit</Text></Col>
              <Col>
                <Text strong style={{ ...NUM, fontSize: 16,
                  color: Number(data.gross_profit) >= 0 ? '#52c41a' : '#cf1322' }}>
                  ₹{fmt(data.gross_profit, 0)}
                </Text>
              </Col>
            </Row>
          </Card>

          <Title level={5} style={{ marginTop: 16 }}>Indirect Expenses</Title>
          <Table size="small" pagination={false} dataSource={data.indirect_expense_lines}
            rowKey="group_code" showHeader={false}
            columns={[
              { dataIndex: 'group_name' },
              { dataIndex: 'amount', align: 'right' as const,
                render: (v: string) => <span style={NUM}>₹{fmt(v, 0)}</span> },
            ]}
            summary={() => (
              <Table.Summary.Row style={{ background: '#fff7e6', fontWeight: 700 }}>
                <Table.Summary.Cell index={0}>Total Indirect Expenses</Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong style={{ ...NUM, color: '#cf1322' }}>₹{fmt(data.indirect_expense_total, 0)}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />

          <Card size="small" style={{ marginTop: 16,
            background: Number(data.net_profit) >= 0 ? '#f6ffed' : '#fff1f0',
            borderColor: Number(data.net_profit) >= 0 ? '#b7eb8f' : '#ffa39e' }}>
            <Row justify="space-between">
              <Col><Text strong style={{ fontSize: 16 }}>Net Profit / (Loss)</Text></Col>
              <Col>
                <Text strong style={{ ...NUM, fontSize: 22,
                  color: Number(data.net_profit) >= 0 ? '#52c41a' : '#cf1322' }}>
                  ₹{fmt(data.net_profit, 0)}
                </Text>
              </Col>
            </Row>
          </Card>
        </Card>
      )}
    </>
  )
}


// ─── Balance Sheet ───────────────────────────────────────────────────────
interface BSLine { group_code: string; group_name: string; nature: string; amount: string }
interface BSResponse {
  current_assets: BSLine[]; current_assets_total: string
  fixed_assets: BSLine[]; fixed_assets_total: string; total_assets: string
  current_liabilities: BSLine[]; current_liabilities_total: string
  long_term_liabilities: BSLine[]; long_term_liabilities_total: string
  equity: BSLine[]; equity_total: string
  total_liabilities_equity: string
  period_pl_carryforward: string
}

function BSTab({ plantId }: { plantId: string }) {
  const [asOf, setAsOf] = useState<Dayjs>(dayjs())

  const { data, isLoading } = useQuery<BSResponse>({
    queryKey: ['bs', plantId, asOf.format('YYYY-MM-DD')],
    queryFn: () => apiClient.get('/accounting/balance-sheet', {
      params: { plant_id: plantId, as_of: asOf.format('YYYY-MM-DD') },
    }).then(r => r.data),
    enabled: !!plantId,
  })

  const renderSection = (title: string, lines: BSLine[], total: string, color: string) => (
    <>
      <Title level={5}>{title}</Title>
      <Table size="small" pagination={false} dataSource={lines} rowKey="group_code"
        showHeader={false}
        columns={[
          { dataIndex: 'group_name' },
          { dataIndex: 'amount', align: 'right' as const,
            render: (v: string) => <span style={NUM}>₹{fmt(v, 0)}</span> },
        ]}
        summary={() => (
          <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 700 }}>
            <Table.Summary.Cell index={0}>Subtotal</Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              <Text strong style={{ ...NUM, color }}>₹{fmt(total, 0)}</Text>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </>
  )

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={6}>
          <DatePicker value={asOf} onChange={(v) => v && setAsOf(v)}
            style={{ width: '100%' }} />
        </Col>
      </Row>

      {isLoading && <Spin />}
      {data && (
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Card size="small" style={{ background: '#f6ffed' }}
              title="Assets" headStyle={{ background: '#52c41a', color: '#fff' }}>
              {renderSection('Current Assets', data.current_assets,
                data.current_assets_total, '#52c41a')}
              <div style={{ marginTop: 16 }} />
              {renderSection('Fixed Assets', data.fixed_assets,
                data.fixed_assets_total, '#52c41a')}
              <Card size="small" style={{ marginTop: 12, background: '#52c41a' }}>
                <Row justify="space-between">
                  <Col><Text strong style={{ color: '#fff' }}>TOTAL ASSETS</Text></Col>
                  <Col>
                    <Text strong style={{ ...NUM, color: '#fff', fontSize: 18 }}>
                      ₹{fmt(data.total_assets, 0)}
                    </Text>
                  </Col>
                </Row>
              </Card>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" style={{ background: '#fff1f0' }}
              title="Liabilities + Equity" headStyle={{ background: '#cf1322', color: '#fff' }}>
              {renderSection('Current Liabilities', data.current_liabilities,
                data.current_liabilities_total, '#cf1322')}
              <div style={{ marginTop: 16 }} />
              {renderSection('Long-term Liabilities', data.long_term_liabilities,
                data.long_term_liabilities_total, '#cf1322')}
              <div style={{ marginTop: 16 }} />
              {renderSection('Equity', data.equity, data.equity_total, '#cf1322')}
              {Math.abs(Number(data.period_pl_carryforward)) > 0.01 && (
                <Card size="small" style={{ marginTop: 12, background: '#fff7e6',
                  borderColor: '#ffd591' }}>
                  <Row justify="space-between">
                    <Col><Text strong>Period P&amp;L (carry-forward)</Text></Col>
                    <Col>
                      <Text strong style={{ ...NUM,
                        color: Number(data.period_pl_carryforward) >= 0 ? '#52c41a' : '#cf1322' }}>
                        ₹{fmt(data.period_pl_carryforward, 0)}
                      </Text>
                    </Col>
                  </Row>
                </Card>
              )}
              <Card size="small" style={{ marginTop: 12, background: '#cf1322' }}>
                <Row justify="space-between">
                  <Col><Text strong style={{ color: '#fff' }}>TOTAL LIAB + EQUITY</Text></Col>
                  <Col>
                    <Text strong style={{ ...NUM, color: '#fff', fontSize: 18 }}>
                      ₹{fmt(data.total_liabilities_equity, 0)}
                    </Text>
                  </Col>
                </Row>
              </Card>
            </Card>
          </Col>
        </Row>
      )}
    </>
  )
}


// ─── GSTR-3B ─────────────────────────────────────────────────────────────
interface GSTR3BTable31Row {
  nature: string
  taxable_value: string
  igst: string; cgst: string; sgst: string; cess: string
}
interface GSTR3BTable4Row {
  section: string; sub: string
  igst: string; cgst: string; sgst: string; cess: string
}
interface GSTR3BResponse {
  period_year: number; period_month: number
  plant_gstin: string | null
  table_3_1: GSTR3BTable31Row[]
  table_4: GSTR3BTable4Row[]
  net_igst_payable: string
  net_cgst_payable: string
  net_sgst_payable: string
}

function GSTR3BTab({ plantId }: { plantId: string }) {
  const [year, setYear] = useState<number>(dayjs().year())
  const [month, setMonth] = useState<number>(dayjs().month() + 1)

  const { data, isLoading } = useQuery<GSTR3BResponse>({
    queryKey: ['gstr-3b', plantId, year, month],
    queryFn: () => apiClient.get('/accounting/gstr-3b',
      { params: { plant_id: plantId, year, month } }).then(r => r.data),
    enabled: !!plantId,
  })

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }} align="middle">
        <Col>
          <Text strong>Period:</Text>
        </Col>
        <Col>
          <Select value={month} onChange={setMonth} style={{ width: 130 }}
            options={[
              {value: 1, label: 'January'}, {value: 2, label: 'February'},
              {value: 3, label: 'March'}, {value: 4, label: 'April'},
              {value: 5, label: 'May'}, {value: 6, label: 'June'},
              {value: 7, label: 'July'}, {value: 8, label: 'August'},
              {value: 9, label: 'September'}, {value: 10, label: 'October'},
              {value: 11, label: 'November'}, {value: 12, label: 'December'},
            ]} />
        </Col>
        <Col>
          <InputNumber value={year} onChange={(v) => setYear(Number(v) || dayjs().year())}
            min={2020} max={2100} style={{ width: 100 }} />
        </Col>
        {data?.plant_gstin && (
          <Col>
            <Tag color="blue" style={{ fontFamily: 'monospace' }}>
              GSTIN: {data.plant_gstin}
            </Tag>
          </Col>
        )}
      </Row>

      {isLoading && <Spin />}
      {data && (
        <>
          <Title level={5}>3.1 — Outward & Inward Liable to RCM</Title>
          <Table size="small" pagination={false} dataSource={data.table_3_1}
            rowKey="nature" bordered
            columns={[
              { title: 'Nature of Supplies', dataIndex: 'nature' },
              { title: 'Taxable Value', dataIndex: 'taxable_value', align: 'right' as const,
                render: (v: string) => <span style={NUM}>₹{fmt(v, 0)}</span> },
              { title: 'IGST', dataIndex: 'igst', align: 'right' as const,
                render: (v: string) => <span style={NUM}>₹{fmt(v, 0)}</span> },
              { title: 'CGST', dataIndex: 'cgst', align: 'right' as const,
                render: (v: string) => <span style={NUM}>₹{fmt(v, 0)}</span> },
              { title: 'SGST/UTGST', dataIndex: 'sgst', align: 'right' as const,
                render: (v: string) => <span style={NUM}>₹{fmt(v, 0)}</span> },
              { title: 'Cess', dataIndex: 'cess', align: 'right' as const,
                render: (v: string) => <span style={NUM}>₹{fmt(v, 0)}</span> },
            ]}
          />

          <Title level={5} style={{ marginTop: 24 }}>4 — Eligible ITC</Title>
          <Table size="small" pagination={false} dataSource={data.table_4}
            rowKey={(r) => `${r.section}-${r.sub}`} bordered
            columns={[
              { title: 'Section', dataIndex: 'section' },
              { title: 'Sub', dataIndex: 'sub' },
              { title: 'IGST', dataIndex: 'igst', align: 'right' as const,
                render: (v: string) => <span style={NUM}>₹{fmt(v, 0)}</span> },
              { title: 'CGST', dataIndex: 'cgst', align: 'right' as const,
                render: (v: string) => <span style={NUM}>₹{fmt(v, 0)}</span> },
              { title: 'SGST/UTGST', dataIndex: 'sgst', align: 'right' as const,
                render: (v: string) => <span style={NUM}>₹{fmt(v, 0)}</span> },
              { title: 'Cess', dataIndex: 'cess', align: 'right' as const,
                render: (v: string) => <span style={NUM}>₹{fmt(v, 0)}</span> },
            ]}
          />

          <Card size="small" style={{ marginTop: 16, background: '#fff7e6' }}
            title={<Text strong>Net Tax Payable (after ITC set-off)</Text>}>
            <Row gutter={16}>
              <Col xs={8}>
                <Text type="secondary" style={{ fontSize: 11 }}>IGST</Text>
                <div style={{ ...NUM, fontSize: 22, fontWeight: 700, color: '#cf1322' }}>
                  ₹{fmt(data.net_igst_payable, 0)}
                </div>
              </Col>
              <Col xs={8}>
                <Text type="secondary" style={{ fontSize: 11 }}>CGST</Text>
                <div style={{ ...NUM, fontSize: 22, fontWeight: 700, color: '#cf1322' }}>
                  ₹{fmt(data.net_cgst_payable, 0)}
                </div>
              </Col>
              <Col xs={8}>
                <Text type="secondary" style={{ fontSize: 11 }}>SGST</Text>
                <div style={{ ...NUM, fontSize: 22, fontWeight: 700, color: '#cf1322' }}>
                  ₹{fmt(data.net_sgst_payable, 0)}
                </div>
              </Col>
            </Row>
            <Text type="secondary" style={{ fontSize: 11, marginTop: 8, display: 'block' }}>
              ⓘ Standard set-off applied (output − input). Fine-tune in the GST portal
              based on cross-utilisation rules. Copy these values into the portal's
              GSTR-3B form to file the return.
            </Text>
          </Card>
        </>
      )}
    </>
  )
}
